import { request } from './client';
import type {
  AbdmApiCallLog,
  AbdmEventLog,
  AbdmTransactionLog,
  Page,
  TraceTimeline,
  WebhookOutbox,
  WebhookSubscription,
} from '@/types/backend';

/**
 * Tenant Owner API surface — every call hits an `/api/abdm/*` endpoint
 * gated by `@RequireTenant` server-side. The axios client stamps the
 * `X-Tenant-Id` header from the JWT claim's tenantId, so we don't pass
 * tenantId here at all — that's the explicit isolation contract from
 * PR-4 (`tenantScopedEndpoint_resolvesTenantFromContext_notFromForgedQueryParam`).
 *
 * Mutations remain in PR-D; this module is read-only.
 */

// ---------------- Tenant metrics view (existing service in audit pkg) ----------------

export interface MilestoneCounts {
  pass: number;
  fail: number;
  total: number;
}

export interface TenantMetrics {
  tenantId: string;
  from: string;
  to: string;
  /** "live" or "rollup+live-tail" — set by `TenantMetricsService` (PR-8). */
  source: string;
  milestones: Record<string, MilestoneCounts>;
  totalSuccess: number;
  totalFailure: number;
  totalTimeout: number;
  recentEvents: AbdmEventLog[];
}

export const metrics = {
  /**
   * Default window is the backend's "last 7 days" if both bounds are
   * absent. We pass them through anyway so the dashboard's "last 24h" /
   * "last 14d" toggle is a one-line change.
   */
  read: (params?: { from?: string; to?: string }) =>
    request<TenantMetrics>({ url: '/api/abdm/tenant-metrics', method: 'GET', params }),
};

// ---------------- Tenant-scoped audit (PR-4) ----------------

export interface AuditEventQuery {
  milestone?: string;
  status?: string;
  eventType?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

export interface AuditTransactionQuery {
  status?: string;
  apiName?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

export interface ApiCallLogsQuery {
  abdmStatus?: 'OK' | 'ERROR' | 'SKIPPED' | 'N/A';
  page?: number;
  size?: number;
}

export const audit = {
  events: (q: AuditEventQuery) =>
    request<Page<AbdmEventLog>>({ url: '/api/abdm/audit/events', method: 'GET', params: q }),
  transactions: (q: AuditTransactionQuery) =>
    request<Page<AbdmTransactionLog>>({ url: '/api/abdm/audit/transactions', method: 'GET', params: q }),
  trace: (traceId: string) =>
    request<TraceTimeline>({ url: `/api/abdm/audit/trace/${encodeURIComponent(traceId)}`, method: 'GET' }),
  apiCallLogs: (q: ApiCallLogsQuery) =>
    request<Page<AbdmApiCallLog>>({ url: '/api/abdm/audit/api-call-logs', method: 'GET', params: q }),
};

// ---------------- Tenant self-info ----------------

export interface TenantSelf {
  tenantId: string;
  organizationName: string;
  status: string;
  /** Currently-effective env from the bridge state machine. SANDBOX | PRODUCTION. */
  environment: string;
  /** §29 bridge state. null when no creds have been entered yet. */
  bridgeState?: string | null;
  plan?: string;
  integrationMode: 'PLATFORM' | 'ENTERPRISE' | 'SDK';
  /** True for ENTERPRISE/SDK — they bring their own L1 client_id and a per-tenant URL.
   *  False for PLATFORM — they ride the platform_bridge URL. */
  ownsCallbackUrl: boolean;
  /** Per-tenant URL (ENTERPRISE/SDK only); null otherwise. */
  callbackUrl: string | null;
  /** Platform-bridge URL (always populated, for display). */
  platformCallbackUrl: string | null;
  /** Plan→milestone mapping for THIS tenant (e.g. STARTER → ["AUTH","M1"]). */
  planMilestones: string[];
  allowedApis: { apiCode: string; name: string; milestone: string; category?: string; httpMethod?: string; pathPattern?: string }[];
  /** Webhook event types derived from allowed APIs. */
  allowedEventTypes: string[];
}

export const me = {
  tenant: () => request<TenantSelf>({ url: '/api/abdm/me/tenant', method: 'GET' }),
};

// ---------------- Tenant-scoped webhooks ----------------
// Tenant-scoped CRUD over webhook subscriptions + read-only deliveries.
// Backed by /api/abdm/webhooks/* — auto-scoped to the caller's tenant
// via X-Tenant-Id (set on every request by the axios interceptor).

export interface DeliveryListQuery {
  status?: 'PENDING' | 'IN_FLIGHT' | 'SENT' | 'DLQ';
  page?: number;
  size?: number;
}

export interface SubscriptionUpsert {
  url: string;
  /** Required on create; blank on update keeps the existing value. */
  secret?: string;
  secretId?: string;
  /** Empty array / omitted = receive all events. */
  eventTypes?: string[];
  active?: boolean;
  description?: string;
}

export const webhooks = {
  deliveries: (q: DeliveryListQuery) =>
    request<Page<WebhookOutbox>>({ url: '/api/abdm/webhooks/deliveries', method: 'GET', params: q }),
  delivery: (id: string) =>
    request<WebhookOutbox>({ url: `/api/abdm/webhooks/deliveries/${encodeURIComponent(id)}`, method: 'GET' }),
  replay: (id: string) =>
    request<{ id: string; status: string; nextRetryAt: string; maxAttempts: number }>({
      url: `/api/abdm/webhooks/deliveries/${encodeURIComponent(id)}/replay`,
      method: 'POST',
    }),
  subscriptions: () =>
    request<WebhookSubscription[]>({ url: '/api/abdm/webhooks/subscriptions', method: 'GET' }),
  createSubscription: (body: SubscriptionUpsert) =>
    request<WebhookSubscription>({ url: '/api/abdm/webhooks/subscriptions', method: 'POST', data: body }),
  updateSubscription: (id: string, body: Partial<SubscriptionUpsert>) =>
    request<WebhookSubscription>({
      url: `/api/abdm/webhooks/subscriptions/${encodeURIComponent(id)}`,
      method: 'PATCH',
      data: body,
    }),
  deleteSubscription: (id: string) =>
    request<void>({
      url: `/api/abdm/webhooks/subscriptions/${encodeURIComponent(id)}`,
      method: 'DELETE',
    }),
};
